import { Link } from 'react-router-dom';
import { SparklesIcon } from '@heroicons/react/24/outline';

interface PremiumUpsellCardProps {
  /** Name of the gated feature, e.g. "AI check-in analysis" */
  feature: string;
  /** Short description of the value, e.g. "See patterns in your recovery journey" */
  message: string;
  /** Optional callback when the user dismisses the card */
  onDismiss?: () => void;
}

/**
 * Shown in place of an AI analysis result when the user does not have an active
 * premium subscription or trial. Encourages upgrading with a calm, non-aggressive
 * call to action that stays consistent with the app's recovery-focused tone.
 */
export function PremiumUpsellCard({ feature, message, onDismiss }: PremiumUpsellCardProps) {
  return (
    <div className="rounded-2xl border border-teal-500/30 bg-surface-50 dark:bg-surface-900 p-6 shadow-sm">
      {/* Icon + heading row */}
      <div className="flex items-start gap-4 mb-4">
        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-teal-500/10 dark:bg-teal-400/10 flex items-center justify-center">
          <SparklesIcon className="w-5 h-5 text-teal-600 dark:text-teal-400" />
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-teal-600 dark:text-teal-400 mb-1">
            Premium Feature
          </p>
          <h3 className="text-lg font-semibold text-surface-900 dark:text-surface-100 leading-snug">
            This is a Daily Reprieve Premium feature.
          </h3>
        </div>
      </div>

      {/* Feature description */}
      <div className="mb-5 pl-14">
        <p className="text-surface-700 dark:text-surface-300 mb-1">
          <span className="font-medium text-surface-800 dark:text-surface-200">{feature}</span>
          {' '}— {message}
        </p>
        <p className="text-sm text-surface-500 dark:text-surface-400">
          Support the app that supports your recovery.
        </p>
      </div>

      {/* CTA buttons */}
      <div className="flex flex-col sm:flex-row gap-3 pl-14">
        <Link
          to="/settings/billing"
          className="inline-flex items-center justify-center px-5 py-2.5 rounded-xl bg-teal-600 dark:bg-teal-500 text-white font-semibold text-sm hover:bg-teal-700 dark:hover:bg-teal-400 transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 dark:focus:ring-offset-surface-900"
        >
          Upgrade to Premium
        </Link>

        {onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            className="inline-flex items-center justify-center px-5 py-2.5 rounded-xl text-surface-600 dark:text-surface-400 font-medium text-sm hover:text-surface-800 dark:hover:text-surface-200 hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors focus:outline-none focus:ring-2 focus:ring-surface-300 dark:focus:ring-surface-600 focus:ring-offset-2 dark:focus:ring-offset-surface-900"
          >
            Maybe Later
          </button>
        )}
      </div>
    </div>
  );
}

export default PremiumUpsellCard;
